import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Lock, Zap, Globe, TrendingUp, Wallet, 
  ArrowRight, ChevronRight, Check, Star, Users, 
  BarChart3, Layers, Cpu, Database, Key, Eye, ChevronDown,
  Youtube, Instagram
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WalletConnectModal from '../components/WalletConnectModal';
import MatrixRain from '../components/MatrixRain';
import HeroCoin3D from '../components/HeroCoin3D';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { getWalletConnectErrorMessage } from '../services/apiService';
import toast from 'react-hot-toast';
import './Home.css';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Stats data
const stats = [
  { value: '1B', label: 'Total Supply', icon: Globe },
  { value: '75%', label: 'Community Allocation', icon: Users },
  { value: '2-5Y', label: 'LP Lock Duration', icon: BarChart3 },
  { value: 'Fair', label: 'Launch Model', icon: Zap },
];

// Features data
const features = [
  {
    icon: Shield,
    title: 'Fair Launch',
    description: 'No presale, no insider advantage. 75% of supply goes straight to the community.'
  },
  {
    icon: Lock,
    title: 'Self-Custody',
    description: 'Your keys, your crypto. You hold the power — not the matrix.'
  },
  {
    icon: Layers,
    title: 'Liquidity Locked',
    description: 'LP locked for 2–5 years. Built for long-term holders, not rug pulls.'
  },
  {
    icon: Cpu,
    title: 'Community Driven',
    description: 'The community shapes the roadmap. No corporate gatekeepers.'
  },
  {
    icon: Database,
    title: 'Secure & Private',
    description: 'Military-grade encryption keeps your holdings private and under your control.'
  },
  {
    icon: Key,
    title: 'Recovery System',
    description: 'Advanced recovery options so you never lose access to what is yours.'
  },
];

// Services data
const services = [
  {
    title: 'Ledger Security',
    description: 'Secure your holdings with proprietary ledger technology — fair, private, and unstoppable.',
    link: '/ledger',
    featured: true,
    icon: Shield
  },
  {
    title: 'DeFi Trading',
    description: 'Access decentralized exchanges and maximize your trading potential.',
    link: '/trading',
    icon: TrendingUp
  },
  {
    title: 'Staking',
    description: 'Earn passive income by staking your assets with competitive APY rates.',
    link: '/staking',
    icon: Layers
  },
  {
    title: 'Portfolio Tracking',
    description: 'Monitor all your assets in real-time with advanced analytics and insights.',
    link: '/ledger',
    icon: Eye
  },
];

// Crypto logos for carousel
const cryptoLogos = [
  { name: 'Bitcoin', logo: '/Crypto-logos/Bitcoin-Logo.webp' },
  { name: 'Ethereum', logo: '/Crypto-logos/Ethereum-logo.webp' },
  { name: 'BNB', logo: '/Crypto-logos/bnb-logo.webp' },
  { name: 'Solana', logo: '/Crypto-logos/Solana-logo.webp' },
  { name: 'XRP', logo: '/Crypto-logos/XRP-logo.webp' },
  { name: 'Cardano', logo: '/Crypto-logos/Cardano-logo.webp' },
  { name: 'Polygon', logo: '/Crypto-logos/matic-token-icon.webp' },
  { name: 'Polkadot', logo: '/Crypto-logos/Polkadot-logo.webp' },
  { name: 'Chainlink', logo: '/Crypto-logos/Chainlink-logo.webp' },
  { name: 'Litecoin', logo: '/Crypto-logos/litecoin.webp' },
  { name: 'Uniswap', logo: '/Crypto-logos/Uniswap-logo.webp' },
  { name: 'Arbitrum', logo: '/Crypto-logos/Arbitrum-logo.webp' },
  { name: 'Optimism', logo: '/Crypto-logos/Optimism.webp' },
  { name: 'USDT', logo: '/Crypto-logos/SizeXL-TypeCoin-AssetUSDT.webp' },
  { name: 'USDC', logo: '/Crypto-logos/USD_Coin_icon.webp' },
  { name: 'DAI', logo: '/Crypto-logos/Dai-logo.webp' },
  { name: 'Dogecoin', logo: '/Crypto-logos/Doge-Logo.webp' },
  { name: 'Shiba Inu', logo: '/Crypto-logos/shiba.webp' },
  { name: 'TRON', logo: '/Crypto-logos/Tron-Logo.webp' },
  { name: 'WBTC', logo: '/Crypto-logos/Wrapped-Bitcoin-logo-1.webp' },
];

const trustedCompanies = [
  { name: 'Dropbox', logo: '/Trusted-companies/Dropbox.png' },
  { name: 'Gitlab', logo: '/Trusted-companies/Gitlab.png' },
  { name: 'Jotform', logo: '/Trusted-companies/jotform.png' },
  { name: 'Krisp', logo: '/Trusted-companies/krisp.png' },
  { name: 'Lattice', logo: '/Trusted-companies/lattice.png' },
  { name: 'Pendo', logo: '/Trusted-companies/pendo.png' },
  { name: 'Pingdom', logo: '/Trusted-companies/pingdom.png' },
  { name: 'Prismic', logo: '/Trusted-companies/prismic.png' },
  { name: 'Voiceflow', logo: '/Trusted-companies/voiceflow.png' },
];

// Testimonials
const testimonials = [
  {
    name: 'Alex Chen',
    role: 'Crypto Investor',
    content: 'FTMX transformed how I manage my portfolio. The security features are unmatched.',
    avatar: 'AC'
  },
  {
    name: 'Sarah Miller',
    role: 'DeFi Trader',
    content: 'Finally, a token that puts the community first. Fair launch, zero gatekeepers.',
    avatar: 'SM'
  },
  {
    name: 'Michael Ross',
    role: 'Blockchain Developer',
    content: 'The multi-chain support is incredible. All my assets in one secure place.',
    avatar: 'MR'
  },
  {
    name: 'Emily Watson',
    role: 'Portfolio Manager',
    content: 'Best wallet security I have ever used. The backup features give me complete peace of mind.',
    avatar: 'EW'
  },
  {
    name: 'David Park',
    role: 'Crypto Enthusiast',
    content: 'Switching to FTMX was the best decision. My assets have never felt safer.',
    avatar: 'DP'
  },
  {
    name: 'Jessica Liu',
    role: 'DeFi Investor',
    content: 'The interface is so intuitive and the cross-chain functionality is seamless. Highly recommend!',
    avatar: 'JL'
  },
];

const heroMetrics = [
  { value: '1B', label: 'Total Supply', sub: 'FTMX Tokens' },
  { value: '75%', label: 'Community Allocation', sub: 'Fair Launch' },
  { value: '2-5Y', label: 'LP Lock Duration', sub: 'Liquidity Locked' },
];

const heroTickerItems = [
  { text: 'FAIR LAUNCH', accent: false },
  { text: 'COMMUNITY DRIVEN', accent: true },
  { text: 'FTMX TOKEN', accent: false, highlight: 'FTMX' },
  { text: 'FUCK THE MATRIX', accent: true },
  { text: 'UNSTOPPABLE', accent: false },
];

const videoTabs = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
];

const youtubeVideos = [
  {
    id: 'suHBx35YLK4',
    title: 'Josh Nelson | Episode 178 | The Breuniverse',
    description: 'Discover the future of decentralized finance',
  },
];

const instagramVideos = [
  {
    url: 'https://www.instagram.com/reel/DNNYHtiSqmH/',
    embedUrl: 'https://www.instagram.com/reel/DNNYHtiSqmH/embed/',
    title: 'Behind the Scenes',
    description: 'Exclusive content from our community',
  },
];

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoTab, setVideoTab] = useState('youtube');
  const { connectWalletUser, user } = useAuth();
  const navigate = useNavigate();

  // Keep returning users signed in and send them straight to dashboard.
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleWalletConnect = async (walletData) => {
    try {
      const result = await connectWalletUser(walletData);
      if (result?.error === 'suspended') {
        setIsModalOpen(false);
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }

      setIsModalOpen(false);
      if (result?.isNewUser) {
        toast.success('Wallet connected successfully!');
        navigate('/backup-success');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getWalletConnectErrorMessage(error));
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          <MatrixRain />
          <div className="hero-vignette"></div>
        </div>

        <Navbar embedded />
        
        <div className="container hero-layout">
          <motion.div 
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 className="hero-title" variants={fadeInUp}>
              <span className="hero-title-line">FUCK</span>
              <span className="hero-title-line">THE</span>
              <span className="hero-title-line hero-title-accent">MATRIX</span>
            </motion.h1>
            
            <motion.div className="hero-description-wrap" variants={fadeInUp}>
              <p className="hero-description">
                Fair. Secure. Unstoppable.{' '}
                <span className="hero-description-accent">Built for the people.</span>
              </p>
            </motion.div>
            
            <motion.div className="hero-actions" variants={fadeInUp}>
              <Link to="/get-started" className="hero-btn-buy">
                BUY NOW
                <ArrowRight size={18} />
              </Link>
              <button
                type="button"
                className="hero-btn-whitepaper"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started
                <ArrowRight size={18} />
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
          >
            <HeroCoin3D />
          </motion.div>
        </div>

        <motion.div
          className="hero-metrics"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
        >
          {heroMetrics.map((metric) => (
            <div key={metric.label} className="hero-metric-item">
              <span className="hero-metric-value">{metric.value}</span>
              <span className="hero-metric-label">{metric.label}</span>
              <span className="hero-metric-sub">{metric.sub}</span>
            </div>
          ))}
        </motion.div>

        <div className="hero-ticker" aria-hidden="true">
          <div className="hero-ticker-track">
            {[...heroTickerItems, ...heroTickerItems].map((item, index) => (
              <span
                key={`${item.text}-${index}`}
                className={`hero-ticker-item${item.accent ? ' is-accent' : ''}`}
              >
                {item.highlight ? (
                  <>
                    <span className="hero-ticker-highlight">{item.highlight}</span>
                    {' TOKEN'}
                  </>
                ) : (
                  item.text
                )}
                <span className="hero-ticker-dot" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Community Videos Section */}
      <section className="videos-section">
        <div className="videos-deco videos-deco-left" aria-hidden="true"></div>
        <div className="videos-deco videos-deco-right" aria-hidden="true"></div>

        <div className="videos-container">
          <motion.div
            className="videos-header"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <p className="videos-eyebrow">Latest Content</p>
            <h2 className="videos-title">
              <span className="videos-title-accent">Community</span> Videos
            </h2>
            <p className="videos-subtitle">
              Watch our latest drops and community highlights from across the web
            </p>
          </motion.div>

          <motion.div
            className="videos-tabs"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="videos-tabs-inner">
              {videoTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`videos-tab${videoTab === tab.id ? ' active' : ''}`}
                  onClick={() => setVideoTab(tab.id)}
                >
                  {videoTab === tab.id && (
                    <motion.span
                      layoutId="video-tab-active"
                      className="videos-tab-bg"
                      transition={{ type: 'spring', bounce: 0.18, duration: 0.55 }}
                    />
                  )}
                  <span className="videos-tab-content">
                    {tab.id === 'youtube' ? <Youtube size={16} /> : <Instagram size={16} />}
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {videoTab === 'youtube' ? (
              <motion.div
                key="youtube"
                className="videos-grid"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {youtubeVideos.map((video) => (
                  <div key={video.id} className="video-card">
                    <div className="video-frame">
                      <YouTubeEmbed videoId={video.id} title={video.title} />
                    </div>
                    <div className="video-meta">
                      <div className="video-meta-text">
                        <h3>{video.title}</h3>
                        <p>{video.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="instagram"
                className="videos-grid"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {instagramVideos.map((video) => (
                  <div key={video.url} className="video-card video-card-instagram">
                    <div className="video-frame video-frame-instagram">
                      <iframe
                        src={video.embedUrl}
                        title={video.title}
                        loading="lazy"
                        allowFullScreen
                      />
                    </div>
                    <div className="video-meta">
                      <div className="video-meta-text">
                        <h3>{video.title}</h3>
                        <p>{video.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            className="videos-badge"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="videos-badge-dot" />
            <span>Break Free from Financial Slavery — Join the FTMX Revolution</span>
          </motion.div>
        </div>
      </section>

      {/* Explore Crypto Section */}
      <section className="crypto-carousel-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">
              <Globe size={14} />
              Supported Assets
            </span>
            <h2>Explore Crypto</h2>
            <p>Trade and manage 500+ cryptocurrencies across multiple chains.</p>
          </motion.div>
        </div>
        
        <div className="crypto-carousel-wrapper">
          <div className="crypto-carousel">
            <div className="crypto-track">
              {[...cryptoLogos, ...cryptoLogos].map((crypto, index) => (
                <div key={index} className="crypto-item">
                  <div className="crypto-logo-wrapper">
                    <img src={crypto.logo} alt={crypto.name} />
                  </div>
                  <span className="crypto-name">{crypto.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="carousel-fade-left"></div>
          <div className="carousel-fade-right"></div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card glass-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon size={24} className="stat-icon" />
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="trusted-by-section">
        <div className="container">
          <motion.div 
            className="trusted-by-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="trusted-by-title">Trusted by</h2>
            <div className="trusted-carousel-wrapper">
              <div className="trusted-lane trusted-lane-left">
                <div className="trusted-track trusted-track-left">
                  <div className="trusted-group">
                    {trustedCompanies.map((company) => (
                      <div className="trusted-item" key={`left-1-${company.name}`}>
                        <div className="trusted-logo-wrapper">
                          <img src={company.logo} alt={company.name} loading="eager" decoding="auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="trusted-group">
                    {trustedCompanies.map((company) => (
                      <div className="trusted-item" key={`left-2-${company.name}`}>
                        <div className="trusted-logo-wrapper">
                          <img src={company.logo} alt={company.name} loading="eager" decoding="auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="trusted-lane trusted-lane-right">
                <div className="trusted-track trusted-track-right">
                  <div className="trusted-group">
                    {[...trustedCompanies].reverse().map((company) => (
                      <div className="trusted-item" key={`right-1-${company.name}`}>
                        <div className="trusted-logo-wrapper">
                          <img src={company.logo} alt={company.name} loading="eager" decoding="auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="trusted-group">
                    {[...trustedCompanies].reverse().map((company) => (
                      <div className="trusted-item" key={`right-2-${company.name}`}>
                        <div className="trusted-logo-wrapper">
                          <img src={company.logo} alt={company.name} loading="eager" decoding="auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Your Assets, Your Control. <br />Fully Decentralized.</h2>
              <p>
                Unlike traditional Web2 wallets that share and access your data, FTMX 
                keeps you fully decentralized with zero third-party data sharing. Your transactions 
                remain private, untraceable, and completely under your control.
              </p>
              <p>
                No risk of account suspension, freezing, or hacking by centralized entities. 
                With QFS-level security and inheritance features, your digital wealth is protected 
                for generations, accessible both online and offline.
              </p>
            </motion.div>
            <motion.div 
              className="about-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="about-image-box">
                <img src="/dapps-screenshot.png" alt="FTMX App Interface" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Our Services</span>
            <h2>Everything You Need<br />in One Platform</h2>
            <p>From secure storage to advanced trading, we've got you covered.</p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className={`service-card glass-card ${service.featured ? 'featured' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {service.featured && <div className="featured-badge">Featured</div>}
                <div className="service-icon">
                  <service.icon size={28} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={service.link} className="service-link">
                  Learn More <ChevronRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Why FTMX</span>
            <h2>Fair. Secure.<br />Unstoppable.</h2>
            <p>Built for the people — not the matrix.</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ledger CTA Section */}
      <section className="ledger-cta-section">
        <div className="container">
          <motion.div 
            className="ledger-cta glass-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="ledger-cta-content">
              <div className="ledger-icon">
                <Shield size={48} strokeWidth={1.5} />
              </div>
              <h2>Discover Our Ledger Service</h2>
              <p>
                Take control of your crypto. Our proprietary ledger technology
                keeps your holdings secure, private, and truly yours.
              </p>
              <div className="ledger-features">
                <div className="ledger-feature">
                  <Check size={16} />
                  <span>Hardware-level Security</span>
                </div>
                <div className="ledger-feature">
                  <Check size={16} />
                  <span>Multi-signature Support</span>
                </div>
                <div className="ledger-feature">
                  <Check size={16} />
                  <span>Cold Storage Integration</span>
                </div>
              </div>
              <Link to="/ledger" className="btn btn-primary btn-lg">
                Explore Ledger Service
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="ledger-cta-visual">
              <video 
                className="ledger-video"
                autoPlay 
                loop 
                muted 
                playsInline
              >
                <source src="/videos/Service-video.mp4" type="video/mp4" />
              </video>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Getting Started</span>
            <h2>Three Simple Steps</h2>
            <p>Get started with FTMX in minutes.</p>
          </motion.div>

          <div className="steps-grid">
            {[
              { step: '01', title: 'Create Account', desc: 'Click Get Started and connect your wallet to create your secure account.' },
              { step: '02', title: 'Backup Your Wallet', desc: 'Connect and backup your existing wallet to secure your assets with our enhanced protection.' },
              { step: '03', title: 'Start Managing', desc: 'Track, trade, and grow your portfolio with our comprehensive tools.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="step-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="step-number">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                {index < 2 && <div className="step-connector"></div>}
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="steps-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/get-started" className="btn btn-primary btn-lg">
              Get Started Now
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Testimonials</span>
            <h2>Loved by Thousands</h2>
            <p>See what our users have to say about FTMX.</p>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card glass-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="#fff" />)}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.avatar}</div>
                  <div className="author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">FAQ</span>
            <h2>Questions &<br />Answers</h2>
            <p>Everything you need to know about FTMX.</p>
          </motion.div>

          <div className="faq-grid">
            {[
              {
                question: "What is FTMX?",
                answer: "FTMX is a fair-launch community token built for the people. 75% of supply goes to the community, liquidity is locked for 2–5 years, and there is no presale or insider advantage. Fair. Secure. Unstoppable."
              },
              {
                question: "Is my data safe with FTMX?",
                answer: "Absolutely! Your data stays completely private. We use military-grade encryption and QFS (Quantum Financial System) technology. Your transactions are untraceable and your private keys never leave your device."
              },
              {
                question: "Can anyone freeze or block my account?",
                answer: "No. With FTMX, you have 100% control of your funds. Unlike centralized wallets, no third party can suspend, freeze, or block your access. Your assets are truly yours."
              },
              {
                question: "What happens to my crypto if something happens to me?",
                answer: "Your family can securely access your funds using your recovery phrase. Unlike traditional banks where money might be lost, FTMX ensures your digital wealth stays with your loved ones."
              },
              {
                question: "Which cryptocurrencies are supported?",
                answer: "We support 50+ blockchain networks including Bitcoin, Ethereum, Solana, Polygon, BNB Chain, and all ERC-20 tokens. Over 10,000 tokens are available across multiple chains."
              },
              {
                question: "How do I get started?",
                answer: "Simply click 'Get Started', connect your existing wallet or create a new one, and backup your credentials. Your assets will be protected with our enhanced security in minutes."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <details className="faq-details">
                  <summary className="faq-question">
                    <span>{faq.question}</span>
                    <ChevronDown className="faq-icon" size={20} />
                  </summary>
                  <p className="faq-answer">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section" style={{ backgroundImage: `url('/CTA-button-BG.png')` }}>
        <div className="container">
          <motion.div 
            className="final-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to break free from the matrix?</h2>
            <p>
              Join the FTMX community. Fair launch, community driven, built for the people.
            </p>
            <div className="final-cta-actions">
              <Link to="/get-started" className="btn btn-primary btn-lg">
                Get Started Now
                <ArrowRight size={20} />
              </Link>
              <button 
                className="btn btn-secondary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect Wallet
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Wallet Connect Modal */}
      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="register"
      />
    </div>
  );
};

export default Home;
